import { useEffect, useState } from 'react'
import {
  Button, Caption1, Field, Input, Link, MessageBar, MessageBarBody,
  Subtitle2, Text, makeStyles, tokens,
} from '@fluentui/react-components'
import { useNavigate } from 'react-router-dom'
import api from '../lib/api'
import { apiErrorMessage } from '../lib/apiError'

interface AICredential {
  access_token: string
  expires_at: string
  scope: string
}

const useStyles = makeStyles({
  panel: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
    maxWidth: '1080px', marginTop: tokens.spacingVerticalXL,
    padding: tokens.spacingHorizontalL, borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  actions: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
})

export function AIAccessPanel() {
  const styles = useStyles()
  const navigate = useNavigate()
  const [credential, setCredential] = useState<AICredential | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    if (!credential) return
    const timer = window.setTimeout(() => {
      setCredential(null)
      setNotice('Your AI credential has expired. Generate a new one to reconnect.')
    }, Math.max(0, Date.parse(credential.expires_at) - Date.now()))
    return () => window.clearTimeout(timer)
  }, [credential])

  async function generate() {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const response = await api.post<AICredential>('/auth/ai-token')
      setCredential(response.data)
      setNotice('Created. Any previous AI credential for your account was replaced.')
    } catch (err) {
      setError(`Could not generate AI access. ${apiErrorMessage(err, 'Try again.')}`)
    } finally {
      setBusy(false)
    }
  }

  async function revoke() {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await api.delete('/auth/ai-token')
      setCredential(null)
      setNotice('AI access revoked. Your web session is unchanged.')
    } catch (err) {
      setError(`Could not revoke AI access. ${apiErrorMessage(err, 'Try again.')}`)
    } finally {
      setBusy(false)
    }
  }

  async function copyCredential() {
    if (!credential) return
    setError('')
    try {
      await navigator.clipboard.writeText(credential.access_token)
      setNotice('Copied. Paste only into your approved AI client credential prompt, never into chat.')
    } catch {
      setError('Clipboard access failed. Select the credential field and copy it manually.')
    }
  }

  return (
    <section className={styles.panel} aria-labelledby="ai-access-title">
      <Subtitle2 as="h2" id="ai-access-title">AI access</Subtitle2>
      <Text>
        Find opportunities, compare projects and check your applications using an approved
        AI client. It reads only what your account may see and changes nothing, except the
        planning page for admins.
      </Text>
      <Text>
        Connect from the AI tool: run <code>daccelerate_mcp.py login</code> and approve it in
        the browser. Use only an organization-approved client and model.
      </Text>
      <div className={styles.actions}>
        <Link href="/help#ai-access" onClick={(e) => { e.preventDefault(); navigate('/help#ai-access') }}>
          Setup instructions
        </Link>
        <Link href="/ai-access/SKILL.md" download="SKILL.md">Download the DACcelerate skill</Link>
      </div>
      <Caption1>
        Example prompts: &quot;Find opportunities within 4 hours per week&quot;,
        &quot;Compare these two projects&quot;, &quot;What is the next step for my applications?&quot;
      </Caption1>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {notice && <p role="status">{notice}</p>}
      <Caption1>No browser on that machine? Generate a credential here instead.</Caption1>
      <div className={styles.actions}>
        <Button disabled={busy} onClick={() => void generate()}>
          Generate credential
        </Button>
        <Button disabled={busy} onClick={() => void revoke()}>Revoke AI access</Button>
      </div>
      {credential && (
        <>
          <Field label="AI credential"
            hint="Shown only here, not stored in browser storage. Never paste it into chat, a repository or a shared configuration file.">
            <Input type="password" value={credential.access_token} readOnly autoComplete="off" />
          </Field>
          <Caption1>Expires: {new Date(credential.expires_at).toLocaleString()}</Caption1>
          <div className={styles.actions}>
            <Button onClick={() => void copyCredential()}>Copy credential</Button>
            <Button onClick={() => setCredential(null)}>Hide credential</Button>
          </div>
          <Caption1>Hiding or leaving this page does not revoke access. Use Revoke AI access to stop new requests.</Caption1>
        </>
      )}
    </section>
  )
}
